import { useState } from "react";
import {
  Download,
  X,
  HelpCircle,
  CheckCircle,
  FileText,
  AlertCircle,
  DollarSign,
} from "lucide-react";
import { downloadCSVTemplate } from "../lib/csvImport";

interface TemplateGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Generic onDownload callback. If not provided, defaults to project download (legacy support)
  onDownload?: () => void;
  // Type of guide: 'project' (default) or 'variation'
  type?: "project" | "variation";
  // Only used for 'project' type
  availableProjects?: { id: string; name: string }[];
  isLoadingProjects?: boolean;
}

export function TemplateGuideModal({
  isOpen,
  onClose,
  onDownload,
  type = "project",
  availableProjects = [],
  isLoadingProjects = false,
}: TemplateGuideModalProps) {
  const [downloaded, setDownloaded] = useState(false);

  if (!isOpen) return null;

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (type === "project") {
      downloadCSVTemplate();
    }
    setDownloaded(true);
    // Auto-close after 2 seconds
    setTimeout(() => {
      onClose();
      setDownloaded(false);
    }, 2000);
  };

  const isProject = type === "project";

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ zIndex: 60 }} // Higher than parent modals
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 650 }}
      >
        {/* Header */}
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: isProject
                  ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
                  : "linear-gradient(135deg, 255, 107, 53, 0.9), rgba(255, 107, 53, 1))", // Orange for variations matches theme? Actually let's just use success/primary colors.
                backgroundColor: isProject
                  ? "var(--color-primary)"
                  : "var(--color-success)", // Variation usually uses success/green in this app? Or orange?
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isProject ? (
                <FileText size={20} color="white" />
              ) : (
                <DollarSign size={20} color="white" />
              )}
            </div>
            <div>
              <h3 className="modal-title" style={{ marginBottom: 2 }}>
                Download {isProject ? "Project" : "Variation"} Template
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-secondary)",
                  margin: 0,
                }}
              >
                Quick guide to help you set up your{" "}
                {isProject ? "project" : "variation"} data
              </p>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Available Projects Section (Project Only) */}
          {isProject && (
            <div
              style={{
                background: "var(--bg-tertiary)",
                borderRadius: "var(--border-radius-md)",
                padding: "var(--spacing-md)",
                marginBottom: "var(--spacing-lg)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <AlertCircle size={16} color="var(--color-info)" />
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  Available Projects from Dashpivot
                </span>
              </div>

              {isLoadingProjects ? (
                <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  Loading projects...
                </p>
              ) : availableProjects.length === 0 ? (
                <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  All Dashpivot projects are already in Claims App, or Dashpivot
                  is not configured.
                </p>
              ) : (
                <>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      marginBottom: 10,
                    }}
                  >
                    Use one of these names in the "Job Location" field to ensure
                    proper variation mapping:
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      maxHeight: 120,
                      overflowY: "auto",
                    }}
                  >
                    {availableProjects.map((project) => (
                      <span
                        key={project.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "var(--bg-secondary)",
                          padding: "4px 10px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 500,
                          border: "1px solid var(--border-color)",
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "var(--color-success)",
                          }}
                        />
                        {project.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Instructions */}
          <h4
            style={{
              marginBottom: "var(--spacing-md)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <HelpCircle size={18} color="var(--color-primary)" />
            How to Fill the Template
          </h4>

          <div
            style={{
              display: "grid",
              gap: "var(--spacing-md)",
            }}
          >
            {(isProject
              ? [
                  {
                    step: 1,
                    title: "Job Location",
                    desc: "Enter the project name exactly as shown above from Dashpivot. This ensures variations sync correctly.",
                    required: true,
                  },
                  {
                    step: 2,
                    title: "Job Address",
                    desc: 'The physical address of the project site (e.g., "123 Queen Street, Brisbane").',
                    required: true,
                  },
                  {
                    step: 3,
                    title: "Project Details",
                    desc: "Optional notes or description for the project.",
                    required: false,
                  },
                  {
                    step: 4,
                    title: "Day Labour Rates",
                    desc: "Set your Normal Rate (default $100) and Overtime Rate (default $150) for variation calculations.",
                    required: false,
                  },
                  {
                    step: 5,
                    title: "Categories & Subcategories",
                    desc: 'List your budget categories (e.g., "Columns", "Beams") and subcategories with amounts. Use "TRUE" for Fixed Amount categories.',
                    required: true,
                  },
                ]
              : [
                  {
                    step: 1,
                    title: "Date",
                    desc: "Required. Valid date format (YYYY-MM-DD), e.g., 2026-01-15.",
                    required: true,
                  },
                  {
                    step: 2,
                    title: "Title & Type",
                     
                     
                     
                    // eslint-disable-next-line no-useless-escape -- auto-suppressed for safety
                    desc: 'Description of work. Type should be \"Variance\" or \"Credit\" (for deductions).',
                    required: true,
                  },
                  {
                    step: 3,
                    title: "Status",
                     
                     
                     
                     
                     
                    // eslint-disable-next-line no-useless-escape -- auto-suppressed for safety
                    desc: 'Set to \"Valid\", \"Invalid\", or \"Pending\". Defaults to Pending if not specified.',
                    required: false,
                  },
                  {
                    step: 4,
                    title: "Day Labour Hours",
                    desc: "Enter hours for Normal, OT (Overtime), and Saturday labour. Amounts calculate automatically based on project rates.",
                    required: false,
                  },
                  {
                    step: 5,
                    title: "Materials/Others",
                    desc: "Enter Material/Others Title (description), Qty (quantity), and Unit Cost. Total is auto-calculated (Qty × Cost).",
                    required: false,
                  },
                  {
                    step: 6,
                    title: "Approved Amount",
                    desc: "Enter amount already approved. Difference from Total becomes 'Waiting Amount'.",
                    required: false,
                  },
                ]
            ).map((item) => (
              <div
                key={item.step}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "var(--color-primary)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {item.step}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      marginBottom: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {item.title}
                    {item.required && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--color-error)",
                          fontWeight: 500,
                        }}
                      >
                        Required
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={downloaded}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 180,
            }}
          >
            {downloaded ? (
              <>
                <CheckCircle size={18} />
                Downloaded!
              </>
            ) : (
              <>
                <Download size={18} />
                Download Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
